/**
 * Files — `modules/files/routes.ts` `clientFilesRoutes`.
 *
 * `GET /api/clients/me/files` answers all three of the page's tabs in one call: the
 * month, the projects and the library. The segmented control switches a search
 * parameter, not a request (`readmodels/files-client.ts` says why).
 *
 * The bundle is a download rather than a query — `bundleHref(period)` is a URL a
 * link points at, so the browser streams the ZIP and no JavaScript holds 3.8 GB.
 */

import type { FileReviewInput } from '@fareast/shared'

import { useApiMutation, useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'

/** `GET /api/clients/me/files?period=` — the month, the projects and the library. */
export function useFilesPage(
  period?: string,
  options: { readonly enabled?: boolean; readonly inline?: boolean } = {},
) {
  return useApiQuery({
    key: queryKeys.files.page(period),
    request: (api) =>
      api.rest.clients.me.files.$get({ query: period === undefined ? {} : { period } }),
    ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  })
}

/**
 * "Download August" — `GET /api/clients/me/files/bundle?period=`.
 *
 * An href rather than a hook. The response is a stream with a
 * `Content-Disposition`, and the browser is the only thing that should hold it.
 */
export function bundleHref(period: string): string {
  return `/api/clients/me/files/bundle?period=${encodeURIComponent(period)}`
}

/** The download URL for one file. `GET /api/files/:id/download` does the gating. */
export function fileDownloadHref(id: string): string {
  return `/api/files/${encodeURIComponent(id)}/download`
}

/**
 * `POST /api/clients/me/files/:id/review` — the client's answer on a deliverable.
 *
 * Not money: `.docs/16-files-calls-reports.md` §A — "approval of a **deliverable**
 * is not money" — so no `Idempotency-Key`, and a double-clicked *Approve* is an
 * honest `Conflict` rather than a replayed answer.
 */
export function useReviewFile() {
  return useApiMutation({
    module: 'files',
    request: (api, variables: { id: string } & FileReviewInput) => {
      const { id, ...body } = variables
      return api.rest.clients.me.files[':id'].review.$post({ param: { id }, json: body })
    },
  })
}
